import { Document, Page, Text, View } from '@react-pdf/renderer'
import { PDFCoverPage, PDFFooter, PDFHeader } from './pdf-shared'
import { pdfColors, pdfStyles, severityColors } from './pdf-theme'
import { fmtCurrency, fmtDateTime, fmtNumber, fmtPercent, labelSeverity, labelType } from './pdf-utils'

export interface ExecutiveSummaryData {
  tenant: { id: string; name: string; slug: string; plan: string } | null
  generatedAt: string
  kpis: {
    totalUsers: number
    activeUsers30d: number
    totalPurchased: number
    totalAssigned: number
    monthlyCost: number
    monthlyCostAssigned: number
    pendingCount: number
    pendingTotalSaving: number
    appliedCount: number
    appliedTotalSaving: number
  }
  topRecommendations: Array<{
    id: string
    type: string
    severity: string
    title: string
    estimated_monthly_saving: number
    projected_saving: number
    license_display_name: string | null
    user_display_name: string | null
  }>
  costBreakdown: Array<{
    sku_part_number: string
    display_name: string
    total_units: number
    assigned_units: number
    assigned_cost: number
    total_cost: number
  }>
}

export function ExecutiveSummaryPDF({ data }: { data: ExecutiveSummaryData }) {
  const k = data.kpis
  const utilization = k.totalPurchased > 0 ? (k.totalAssigned / k.totalPurchased) * 100 : 0
  const wasteCost = k.monthlyCost - k.monthlyCostAssigned
  const clientName = data.tenant?.name ?? 'Cliente'

  // Top licenses by cost (chart)
  const topLicenses = [...data.costBreakdown]
    .sort((a, b) => b.total_cost - a.total_cost)
    .slice(0, 6)
  const maxLicenseCost = topLicenses[0]?.total_cost ?? 1

  // Top recs by projected saving (chart)
  const topRecsByImpact = [...data.topRecommendations]
    .sort((a, b) => b.projected_saving - a.projected_saving)
    .slice(0, 6)
  const maxProjected = topRecsByImpact[0]?.projected_saving ?? 1

  return (
    <Document
      title={`ROI365 - Resumen Ejecutivo - ${clientName}`}
      author="ROI365"
      subject="Resumen ejecutivo de optimizacion de licencias Microsoft 365"
    >
      <PDFCoverPage
        reportTitle="Resumen Ejecutivo"
        reportDescription="Vision agregada del estado de licencias, ahorros potenciales identificados y resultados obtenidos en la optimizacion del tenant Microsoft 365."
        clientName={clientName}
        generatedAt={fmtDateTime(data.generatedAt)}
      />

      <Page size="A4" style={pdfStyles.page}>
        <PDFHeader title="Resumen Ejecutivo" subtitle={clientName} />
        <PDFFooter />

        <Text style={pdfStyles.h2}>Indicadores clave</Text>
        <View style={pdfStyles.kpiGrid}>
          <KpiCard
            label="Coste mensual total"
            value={fmtCurrency(k.monthlyCost)}
            sub={`${fmtCurrency(k.monthlyCostAssigned)} en asignadas`}
          />
          <KpiCard
            label="Desperdicio mensual"
            value={fmtCurrency(wasteCost)}
            sub="Coste de licencias no asignadas"
          />
          <KpiCard
            label="Utilizacion"
            value={fmtPercent(utilization)}
            sub={`${fmtNumber(k.totalAssigned)} / ${fmtNumber(k.totalPurchased)} licencias`}
          />
          <KpiCard
            label="Usuarios totales"
            value={fmtNumber(k.totalUsers)}
            sub={`${fmtNumber(k.activeUsers30d)} activos en 30 dias`}
          />
          <KpiCard
            label="Ahorro potencial estimado"
            value={fmtCurrency(k.pendingTotalSaving)}
            sub={`${fmtNumber(k.pendingCount)} recomendaciones pendientes`}
            accent="warning"
          />
          <KpiCard
            label="Ahorro proyectado aplicado"
            value={fmtCurrency(k.appliedTotalSaving)}
            sub={`${fmtNumber(k.appliedCount)} recomendaciones aplicadas (ult. ano)`}
            accent="success"
          />
        </View>

        <Text style={pdfStyles.h2}>Top recomendaciones por impacto</Text>
        {topRecsByImpact.length === 0 ? (
          <Text style={pdfStyles.paragraph}>No hay recomendaciones pendientes en este momento.</Text>
        ) : (
          <View style={{ marginTop: 4 }}>
            {topRecsByImpact.map((r) => {
              const pct = (r.projected_saving / maxProjected) * 100
              return (
                <View key={r.id} style={pdfStyles.barChartItem}>
                  <View style={pdfStyles.barChartLabelRow}>
                    <Text style={[pdfStyles.barChartLabel, { maxWidth: '70%' }]}>
                      {r.title.length > 70 ? r.title.slice(0, 67) + '...' : r.title}
                    </Text>
                    <Text style={pdfStyles.barChartValue}>{fmtCurrency(r.projected_saving)}</Text>
                  </View>
                  <View style={pdfStyles.barTrack}>
                    <View style={[pdfStyles.barFill, { width: `${pct}%` }]} />
                  </View>
                </View>
              )
            })}
          </View>
        )}

        {/* Cost distribution */}
        <Text style={pdfStyles.h2}>Distribucion del coste por licencia</Text>
        {topLicenses.length === 0 ? (
          <Text style={pdfStyles.paragraph}>Sin datos de coste disponibles.</Text>
        ) : (
          <View style={{ marginTop: 4 }}>
            {topLicenses.map((l) => {
              const pct = (l.total_cost / maxLicenseCost) * 100
              return (
                <View key={l.sku_part_number} style={pdfStyles.barChartItem}>
                  <View style={pdfStyles.barChartLabelRow}>
                    <Text style={pdfStyles.barChartLabel}>{l.display_name}</Text>
                    <Text style={pdfStyles.barChartValue}>{fmtCurrency(l.total_cost)}</Text>
                  </View>
                  <View style={pdfStyles.barTrack}>
                    <View
                      style={[
                        pdfStyles.barFill,
                        { width: `${pct}%`, backgroundColor: pdfColors.brandAccent1 },
                      ]}
                    />
                  </View>
                  <Text style={{ fontSize: 7, color: pdfColors.textTertiary, marginTop: 1 }}>
                    {fmtNumber(l.assigned_units)} / {fmtNumber(l.total_units)} unidades asignadas
                  </Text>
                </View>
              )
            })}
          </View>
        )}
      </Page>

      {/* Detailed table page */}
      <Page size="A4" style={pdfStyles.page}>
        <PDFHeader title="Resumen Ejecutivo" subtitle={`${clientName} · Detalle de recomendaciones`} />
        <PDFFooter />

        <Text style={pdfStyles.h2}>Top 10 recomendaciones pendientes</Text>
        <View style={pdfStyles.table}>
          <View style={pdfStyles.tableHeaderRow}>
            <Text style={[pdfStyles.tableHeaderCell, { width: '40%' }]}>Recomendacion</Text>
            <Text style={[pdfStyles.tableHeaderCell, { width: '14%' }]}>Tipo</Text>
            <Text style={[pdfStyles.tableHeaderCell, { width: '12%' }]}>Severidad</Text>
            <Text style={[pdfStyles.tableHeaderCell, { width: '17%', textAlign: 'right' }]}>Mensual</Text>
            <Text style={[pdfStyles.tableHeaderCell, { width: '17%', textAlign: 'right' }]}>Proyectado</Text>
          </View>
          {data.topRecommendations.map((r, idx) => (
            <View
              key={r.id}
              style={[pdfStyles.tableRow, idx % 2 === 1 ? pdfStyles.tableRowAlt : {}]}
            >
              <View style={[pdfStyles.tableCell, { width: '40%' }]}>
                <Text style={{ fontSize: 9, fontFamily: 'Helvetica-Bold' }}>{r.title}</Text>
                <Text style={{ fontSize: 8, color: pdfColors.textTertiary, marginTop: 1 }}>
                  {r.license_display_name ?? '—'}
                  {r.user_display_name ? ` · ${r.user_display_name}` : ''}
                </Text>
              </View>
              <Text style={[pdfStyles.tableCell, { width: '14%' }]}>{labelType(r.type)}</Text>
              <Text
                style={[
                  pdfStyles.tableCell,
                  { width: '12%', color: severityColors[r.severity] ?? pdfColors.textPrimary, fontFamily: 'Helvetica-Bold' },
                ]}
              >
                {labelSeverity(r.severity)}
              </Text>
              <Text style={[pdfStyles.tableCell, { width: '17%', textAlign: 'right' }]}>
                {fmtCurrency(r.estimated_monthly_saving)}
              </Text>
              <Text
                style={[
                  pdfStyles.tableCell,
                  { width: '17%', textAlign: 'right', fontFamily: 'Helvetica-Bold', color: pdfColors.brandSecondary },
                ]}
              >
                {fmtCurrency(r.projected_saving)}
              </Text>
            </View>
          ))}
        </View>

        <Text style={[pdfStyles.small, { marginTop: 12 }]}>
          El ahorro proyectado considera el periodo restante hasta el vencimiento de la licencia
          asociada (o 12 meses por defecto cuando la fecha no esta informada).
        </Text>
      </Page>
    </Document>
  )
}

function KpiCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string
  value: string
  sub?: string
  accent?: 'success' | 'warning' | 'default'
}) {
  const borderColor =
    accent === 'success' ? pdfColors.success : accent === 'warning' ? pdfColors.warning : pdfColors.brandSecondary
  return (
    <View style={[pdfStyles.kpiCard, { borderLeftColor: borderColor }]}>
      <Text style={pdfStyles.kpiCardLabel}>{label}</Text>
      <Text style={pdfStyles.kpiCardValue}>{value}</Text>
      {sub ? <Text style={pdfStyles.kpiCardSub}>{sub}</Text> : null}
    </View>
  )
}
